import type { JsonObject, JsonValue } from '#layers/data-model/public';
import type { AiContextItem } from './context';

export type ChatRole = 'user' | 'assistant';

export interface ChatAnnotation {
	startIndex: number;
	endIndex: number;
	title: string;
	url: string;
}

/**
 * A single block in a chat message. Messages are composed from one or more typed blocks
 * rather than a single free-form string so they can represent text, reasoning, tool calls,
 * and tool results in a structured way.
 */
export interface ContentBlock<TType extends string = string> {
	type: TType;
}

export interface InputTextContentBlock extends ContentBlock<'text'> {
	text: string;
}

export interface OutputTextContentBlock extends InputTextContentBlock {
	annotations: ChatAnnotation[];
}

export interface ThinkingContentBlock extends ContentBlock<'thinking'> {
	thinking: string;
}

export interface BaseToolUseContentBlock<
	TName extends string = string,
	TInput = JsonValue,
> extends ContentBlock<'toolUse'> {
	id: string;
	name: TName;
	input: TInput | null;
	partialInputText?: string;
}

// The properties here are optional as they might be streaming in during
// time of use on the client side
export interface ToolExecuteInput extends JsonObject {
	name?: string;
	intent?: string;
	input?: JsonValue;
}

export interface ToolExecuteContentBlock
	extends BaseToolUseContentBlock<'tool_execute', ToolExecuteInput> {}

export interface UnknownToolUseContentBlock
	extends BaseToolUseContentBlock<string, JsonValue> {}

export type ToolUseContentBlock =
	| ToolExecuteContentBlock
	| UnknownToolUseContentBlock;

export function isToolExecuteContentBlock(
	block: ToolUseContentBlock,
): block is ToolExecuteContentBlock {
	return block.name === 'tool_execute';
}

export interface ToolResultContentBlock extends ContentBlock<'toolResult'> {
	toolUseId: string;
	content: ChatToolResultContent[];
	isError: boolean;
}

export const TOOL_RISKS = ['readOnly', 'mutating', 'destructive'] as const;

export type ToolRisk = typeof TOOL_RISKS[number];

export const TOOL_APPROVAL_LEVELS = [
	'askAlways',
	'askMutating',
	'askDestructive',
	'allowAll',
] as const;

export type ToolApprovalLevel = typeof TOOL_APPROVAL_LEVELS[number];

export interface ToolApprovalRequestContentBlock extends ContentBlock<'toolApprovalRequest'> {
	toolCallId: string;
	targetRisk: ToolRisk;
	approvalLevel: ToolApprovalLevel;
	toolExecute: {
		name: string;
		intent: string;
		input: JsonValue;
	};
}

export interface ToolApprovalResponseContentBlock extends ContentBlock<'toolApprovalResponse'> {
	toolCallId: string;
	approved: boolean;
	reason?: string;
}

export type ChatInputContentBlock =
	| InputTextContentBlock
	| ThinkingContentBlock
	| ToolUseContentBlock
	| ToolResultContentBlock
	| ToolApprovalResponseContentBlock;

export type ChatOutputContentBlock =
	| OutputTextContentBlock
	| ThinkingContentBlock
	| ToolUseContentBlock
	| ToolResultContentBlock
	| ToolApprovalRequestContentBlock;

export type ChatContentBlock = ChatInputContentBlock | ChatOutputContentBlock;

export type ChatContentBlockType = ChatContentBlock['type'];

export interface ToolResultContent<TType extends string = string> {
	type: TType;
}

export interface TextToolResultContent extends ToolResultContent<'text'> {
	text: string;
}

export interface JsonToolResultContent extends ToolResultContent<'json'> {
	data: JsonValue;
}

export type ChatToolResultContent = TextToolResultContent | JsonToolResultContent;

export type ChatToolResultContentType = ChatToolResultContent['type'];

export interface ChatInputMessage {
	role: ChatRole;
	content: ChatInputContentBlock[];
}

export interface ChatOutputMessage {
	role: ChatRole;
	content: ChatOutputContentBlock[];
}

export type ChatMessage = ChatInputMessage | ChatOutputMessage;

export type ChatStopReason = 'endTurn' | 'toolUse' | 'maxTokens';

export interface ChatUsage {
	inputTokens: number;
	outputTokens: number;
}

/**
 * An incremental update for a content block while a streamed response is still being generated.
 * Consumers append or merge these deltas until the corresponding block is finalized.
 */
export interface ContentBlockDelta<TType extends string = string> {
	type: TType;
}

export interface TextContentBlockDelta extends ContentBlockDelta<'textDelta'> {
	text: string;
}

export interface ThinkingContentBlockDelta extends ContentBlockDelta<'thinkingDelta'> {
	thinking: string;
}

export interface InputJsonContentBlockDelta extends ContentBlockDelta<'inputJsonDelta'> {
	partialJson: string;
}

export type ChatContentBlockDelta =
	| TextContentBlockDelta
	| ThinkingContentBlockDelta
	| InputJsonContentBlockDelta;

export type ChatContentBlockDeltaType = ChatContentBlockDelta['type'];

/**
 * A single event emitted by the chat stream protocol.
 * Stream consumers use the `type` discriminant to narrow each event and update local state.
 */
export interface StreamEvent<TType extends string = string> {
	type: TType;
}

export interface MessageStartStreamEvent extends StreamEvent<'messageStart'> {
	messageId: string;
}

export interface ContentBlockStartStreamEvent extends StreamEvent<'contentBlockStart'> {
	blockId: number;
	contentBlock: ChatOutputContentBlock;
}

export interface ContentBlockDeltaStreamEvent extends StreamEvent<'contentBlockDelta'> {
	blockId: number;
	delta: ChatContentBlockDelta;
}

export interface ContentBlockStopStreamEvent extends StreamEvent<'contentBlockStop'> {
	blockId: number;
	contentBlock: ChatOutputContentBlock;
}

export interface MessageStopStreamEvent extends StreamEvent<'messageStop'> {
	messageId: string;
	stopReason: ChatStopReason;
	usage: ChatUsage | null;
}

export interface IterationLimitReachedStreamEvent extends StreamEvent<'iterationLimitReached'> {
	maxIterations: number;
}

export interface ErrorStreamEvent extends StreamEvent<'error'> {
	error: string;
}

export type ChatStreamEvent =
	| MessageStartStreamEvent
	| ContentBlockStartStreamEvent
	| ContentBlockDeltaStreamEvent
	| ContentBlockStopStreamEvent
	| MessageStopStreamEvent
	| IterationLimitReachedStreamEvent
	| ErrorStreamEvent;

export type ChatStreamEventType = ChatStreamEvent['type'];

export interface ChatToolSelection {
	disabled: string[];
}

export interface ChatRequestBody {
	messages: ChatMessage[];
	context: AiContextItem[];
	approvalLevel: ToolApprovalLevel;
	tools?: ChatToolSelection;
}

/**
 * A streamed payload chunk from the server. Normal chunks are structured events and the special
 * `[DONE]` sentinel marks the end of the stream.
 */
export type ChatStreamChunk = ChatStreamEvent | '[DONE]';
